import type { FileCategory, FileData } from "@qendo/profile/files";
import { useState } from "react";
import { ActivityIndicator, Image, type ImageStyle, Pressable, StyleSheet, View, type ViewStyle } from "react-native";
import type { StyleProp } from "react-native/Libraries/StyleSheet/StyleSheet";
import { ImageBox } from "~/components/layout/gallery/ImageBox";
import { Only } from "~/components/permission/Only";
import { useApi } from "~/providers/ApiProvider";
import { useTheme } from "~/providers/ThemeProvider";

export type ThumbnailProps = {
    image: FileData<FileCategory.image>;
    width?: number;
    height?: number;
    expandable?: boolean;
    style?: StyleProp<ViewStyle>;
    imageStyle?: StyleProp<ImageStyle>;
};

export const Thumbnail = ({
    image,
    width = 140,
    height = 140,
    expandable = true,
    style,
    imageStyle,
}: ThumbnailProps) => {
    const { colours } = useTheme();
    const { img } = useApi();
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);

    const dimensions = { width, height };

    const close = () => setShow(false);

    return (
        <>
            <Pressable
                onPress={() => setShow(true)}
                style={[styles.pressable, dimensions, style]}
                disabled={!expandable}
            >
                <Only when={loading}>
                    <View style={[styles.loading, { backgroundColor: colours.backdrop }, dimensions]}>
                        <ActivityIndicator />
                    </View>
                </Only>
                <Image
                    onLoadStart={() => setLoading(true)}
                    onLoadEnd={() => setLoading(false)}
                    source={img(image.url)}
                    width={width}
                    height={height}
                    alt={`${image.name} thumbnail`}
                    resizeMode="cover"
                    style={[dimensions, { objectFit: "cover", resizeMode: "cover" }, imageStyle]}
                />
            </Pressable>
            {show && <ImageBox image={image} onRequestClose={close} />}
        </>
    );
};

const styles = StyleSheet.create({
    pressable: {
        position: "relative",
        overflow: "hidden",
    },
    loading: {
        position: "absolute",
        zIndex: 10,
        alignItems: "center",
        justifyContent: "center",
    },
});
